import { useQuery } from '@apollo/client';
import React, { useEffect, useMemo } from 'react';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import Select, { OptionType, ValueType } from '@atlaskit/select';
import { FetchGroupMembersQuery, GroupMember } from '@css-xp/apd-common';
import { useApplicationContext } from '@css-xp/base/common-context';
import { fetchGroupMembers } from '../../queries';
import { TaskAction } from '../../task-actions/task-actions';

export const groupMemberPlaceholder = 'Select';

interface Props {
  groupId: string | undefined;
  requestType: string | undefined;
  onSelectGroupMember: any;
  selectedGroupMember?: GroupMember;
  showSelectDropdown?: boolean;
  defaultSelectedMember?: GroupMember;
  openModal?: (_param: TaskAction) => void;
}

export const ChangeAssignee = React.memo(
  ({
    groupId,
    requestType,
    onSelectGroupMember,
    selectedGroupMember,
    showSelectDropdown = false,
    defaultSelectedMember,
    openModal,
  }: Props) => {
    const user = useApplicationContext();
    const { data, loading } = useQuery<FetchGroupMembersQuery>(fetchGroupMembers, {
      skip: !user || !groupId || !requestType,
      variables: {
        staffId: user?.userId?.split('@')?.[0],
        groupName: groupId,
        requestType: requestType,
      },
    });

    const getFormattedGroupMembers = useMemo(() => {
      return data?.groupMembers?.map((item: any) => {
        return { label: item?.name, value: item?.userId };
      });
    }, [data]);

    useEffect(() => {
      if (showSelectDropdown && selectedGroupMember?.userId) {
        openModal?.(TaskAction.CHANGE_ASSIGNEE);
      }
    }, [showSelectDropdown, selectedGroupMember]);

    return (
      <div className="group-members-list" data-testid="group-members-list">
        {showSelectDropdown ? (
          <Select
            data-testId="group-members-select-dropdown"
            className="single-select approver-single-select"
            classNamePrefix="react-select"
            options={getFormattedGroupMembers}
            placeholder={groupMemberPlaceholder}
            isDisabled={loading}
            isSearchable={false}
            defaultValue={{ label: defaultSelectedMember?.name, value: defaultSelectedMember?.userId }}
            onChange={(event: ValueType<OptionType>) => {
              onSelectGroupMember({ userId: event?.value, name: event?.label });
            }}
          />
        ) : (
          <DropdownMenu
            testId="group--members-dropdown"
            isLoading={loading}
            trigger={!selectedGroupMember?.name ? groupMemberPlaceholder : selectedGroupMember.name}
            triggerType="button"
          >
            <DropdownItemGroup>
              {data?.groupMembers?.map((item: GroupMember | null) => {
                return (
                  <DropdownItem
                    data-testid={`approver-${item?.userId}`}
                    onClick={() => onSelectGroupMember(item)}
                    key={item?.userId}
                  >
                    {item?.name}
                  </DropdownItem>
                );
              })}
            </DropdownItemGroup>
          </DropdownMenu>
        )}
      </div>
    );
  }
);
